import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import { Add as AddIcon, Visibility, Close, Delete as DeleteIcon, FilterList } from '@mui/icons-material';
import { customerPaymentService } from '../services/customerPaymentService';
import { customerService } from '../services/customerService';
import { format } from 'date-fns';
import { formatDateOnly } from '../utils/dateUtils';
import { formatCurrency } from '../components/CurrencyTextField';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import {
  ProcurementSummaryNotesDense,
  procurementSummaryFieldsRowSx
} from '../components/procurement/ProcurementSummaryBar';

const PAYMENT_LIST_HEADER_FILTERS_INITIAL = {
  paymentNo: '',
  customer: '',
  order: '',
  invoice: '',
  date: '',
  amount: '',
  applied: '',
  unapplied: '',
  method: '',
  recordedBy: '',
  reference: '',
  notes: ''
};

const PAYMENT_LIST_FILTERS_DEFAULTS = {
  customerFilter: '',
  headerColFilters: { ...PAYMENT_LIST_HEADER_FILTERS_INITIAL },
  sortKey: 'payment_number',
  sortDirection: 'desc',
  page: 0
};

function trimHeaderFilterObj(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));
}

function paymentColumnFilterQueryParams(d) {
  const out = {};
  if (d.paymentNo) out.filter_payment_number = d.paymentNo;
  if (d.customer) out.filter_customer = d.customer;
  if (d.order) out.filter_order = d.order;
  if (d.invoice) out.filter_invoice = d.invoice;
  if (d.date) out.filter_date = d.date;
  if (d.amount) out.filter_amount = d.amount;
  if (d.applied) out.filter_applied = d.applied;
  if (d.unapplied) out.filter_unapplied = d.unapplied;
  if (d.method) out.filter_method = d.method;
  if (d.recordedBy) out.filter_recorded_by = d.recordedBy;
  if (d.reference) out.filter_reference = d.reference;
  if (d.notes) out.filter_notes = d.notes;
  return out;
}

const colHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const colHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden',
  '& .MuiTableSortLabel-root': {
    maxWidth: '100%',
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    fontSize: '0.65rem',
    fontWeight: 700,
    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
  }
};

const headerFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

const CustomerPayments = () => {
  const DEFAULT_COL_WIDTHS = {
    paymentNo: 130,
    customer: 200,
    order: 140,
    invoice: 168,
    date: 132,
    amount: 110,
    applied: 100,
    unapplied: 100,
    method: 120,
    recordedBy: 120,
    reference: 140,
    notes: 180,
    actions: 116
  };
  const navigate = useNavigate();
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_customer_payments_list_filters', PAYMENT_LIST_FILTERS_DEFAULTS);
  const customerFilter = listFilters.customerFilter;
  const setCustomerFilter = useCallback((v) => setListFilter('customerFilter', v), [setListFilter]);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const sortKey = listFilters.sortKey;
  const setSortKey = useCallback((v) => setListFilter('sortKey', v), [setListFilter]);
  const sortDirection = listFilters.sortDirection;
  const setSortDirection = useCallback((v) => setListFilter('sortDirection', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
  }, [clearListFilters]);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_customer_payments_rows_per_page'), 10);
      return [50, 100, 200].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() =>
    trimHeaderFilterObj(headerColFilters || PAYMENT_LIST_HEADER_FILTERS_INITIAL)
  );
  const paymentsFilterKeyRef = useRef(null);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_customer_payments_col_widths',
    minWidths: {
      paymentNo: 100,
      customer: 120,
      order: 100,
      invoice: 120,
      date: 100,
      amount: 90,
      applied: 90,
      unapplied: 90,
      method: 100,
      recordedBy: 90,
      reference: 100,
      notes: 120,
      actions: 100
    }
  });

  const paymentAllocated = (p) =>
    parseFloat(p?.amount_allocated ?? 0) ||
    (p?.allocations || []).reduce((s, a) => s + parseFloat(a.amount_applied || 0), 0);

  const paymentUnapplied = (p) => {
    if (p?.amount_unapplied != null) return parseFloat(p.amount_unapplied) || 0;
    return Math.max(0, Math.round((parseFloat(p?.amount || 0) - paymentAllocated(p)) * 100) / 100);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [customerCreditAvailable, setCustomerCreditAvailable] = useState(null);

  const loadCustomers = async () => {
    try {
      const res = await customerService.getAll({ limit: 500 });
      setCustomers(res?.data ?? []);
    } catch {
      setCustomers([]);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(PAYMENT_LIST_HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  const loadPayments = useCallback(async () => {
    const filterKey = [customerFilter || '', JSON.stringify(debouncedHeaderColFilters)].join('|');
    const filterJustChanged =
      paymentsFilterKeyRef.current != null && paymentsFilterKeyRef.current !== filterKey;
    paymentsFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        ...(customerFilter ? { customer_id: customerFilter } : {}),
        ...paymentColumnFilterQueryParams(debouncedHeaderColFilters)
      };
      const res = await customerPaymentService.getAll(params);
      const list = res?.data ?? [];
      const total = typeof res?.total === 'number' ? res.total : list.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setPaymentsTotal(total);
      setPayments(list);
      setCustomerCreditAvailable(
        customerFilter && res?.credit_available != null ? parseFloat(res.credit_available) : null
      );
      setSelectedPayment((prev) => {
        if (prev && list.some((p) => p.id === prev.id)) {
          return list.find((p) => p.id === prev.id) || list[0] || null;
        }
        return list.length > 0 ? list[0] : null;
      });
    } catch (err) {
      console.error('Error loading payments:', err);
      setError(err.response?.data?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [customerFilter, debouncedHeaderColFilters, page, rowsPerPage]);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  /** List/summary: recorded time when available, else payment date (date-only, no TZ shift). */
  const formatPaymentDateTime = (p) => {
    if (p == null) return '—';
    if (p.created_at) {
      try {
        const d = new Date(p.created_at);
        if (Number.isNaN(d.getTime())) return '—';
        return `${format(d, 'MM/dd')} ${format(d, 'hh:mm')}${format(d, 'a').toLowerCase()}`;
      } catch {
        return '—';
      }
    }
    return formatDateOnly(p.payment_date, 'MM/dd') || '—';
  };

  const customerName = (p) =>
    p == null
      ? '—'
      : p.company_name || [p.customer_first_name, p.customer_last_name].filter(Boolean).join(' ') || '—';

  const recordedByName = (p) => {
    if (p == null) return '—';
    const name = [p.recorded_by_first_name, p.recorded_by_last_name].filter(Boolean).join(' ').trim();
    return name || '—';
  };

  const orderDisplayId = (p) => {
    if (p == null) return null;
    return p.resolved_work_order_id != null && p.resolved_work_order_id !== ''
      ? p.resolved_work_order_id
      : p.work_order_id;
  };

  const orderLabel = (p) => {
    if (p == null) return null;
    return (
      p.resolved_work_order_label ||
      p.work_order_identifier ||
      p.work_order_number ||
      (orderDisplayId(p) != null ? `#${orderDisplayId(p)}` : null)
    );
  };

  const allocatedInvoicesList = (p) => {
    let v = p?.allocated_invoices;
    if (v == null) return [];
    if (typeof v === 'string') {
      try {
        v = JSON.parse(v);
      } catch {
        return [];
      }
    }
    return Array.isArray(v) ? v : [];
  };

  const handleRecordSuccess = () => {
    setRecordDialogOpen(false);
    loadPayments();
  };

  const handleConfirmListDelete = async () => {
    if (!deleteTarget?.id) return;
    const removedId = deleteTarget.id;
    try {
      setDeleteSubmitting(true);
      setError('');
      await customerPaymentService.delete(removedId);
      setDeleteTarget(null);
      if (selectedPayment?.id === removedId) setSelectedPayment(null);
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete payment');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedPayments = useMemo(() => {
    const list = [...payments];
    const getter = (row) => {
      switch (sortKey) {
        case 'payment_number': return row.payment_number ?? '';
        case 'customer': return row.company_name || `${row.customer_first_name || ''} ${row.customer_last_name || ''}`.trim();
        case 'payment_date': {
          const raw = row.created_at || row.payment_date;
          return raw ? new Date(raw).getTime() : 0;
        }
        case 'amount': return Number(row.amount) || 0;
        case 'unapplied': return paymentUnapplied(row);
        case 'applied': return paymentAllocated(row);
        case 'payment_method': return row.payment_method ?? '';
        case 'reference_number': return row.reference_number ?? '';
        case 'notes': return row.notes ?? '';
        case 'work_order': {
          const lbl = orderLabel(row);
          return lbl != null && lbl !== '' ? String(lbl) : orderDisplayId(row) != null ? String(orderDisplayId(row)) : '';
        }
        case 'invoices': {
          const inv = allocatedInvoicesList(row);
          return inv.map((x) => x.label).join(', ');
        }
        case 'recorded_by': {
          return recordedByName(row);
        }
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [payments, sortKey, sortDirection]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customer Payments
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRecordDialogOpen(true)}
          >
            Record Payment
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={() => selectedPayment && navigate(`/customer-payments/${selectedPayment.id}`)}
            disabled={!selectedPayment}
          >
            View
          </Button>
          {selectedPayment && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteTarget(selectedPayment)}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Payment Information Bar — same layout as Orders / Quotes list strip */}
      <Paper
        sx={{
          p: 1,
          mb: 1.5,
          minHeight: 104,
          height: 'auto',
          backgroundColor: 'surfaceMuted.main',
          border: 1,
          borderColor: 'borderSubtle.main',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', flex: '0 0 auto', overflow: 'hidden' }}>
            <Box
              sx={{
                ...procurementSummaryFieldsRowSx(true),
                flex: '1 1 auto',
                maxHeight: 64,
                overflowY: 'auto',
              }}
            >
              <Box>
                <Typography variant="caption">Payment #</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedPayment?.payment_number ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Date</Typography>
                <Typography variant="body2">{formatPaymentDateTime(selectedPayment)}</Typography>
              </Box>
              <Box sx={{ minWidth: 180 }}>
                <Typography variant="caption">Customer</Typography>
                <Typography variant="body2">
                  {selectedPayment?.customer_id ? (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontSize: 'inherit' }}
                      onClick={() => navigate(`/customers/${selectedPayment.customer_id}`)}
                    >
                      {customerName(selectedPayment) || `#${selectedPayment.customer_id}`}
                    </Button>
                  ) : selectedPayment ? (
                    customerName(selectedPayment)
                  ) : (
                    '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Order</Typography>
                <Typography variant="body2">
                  {orderDisplayId(selectedPayment) != null ? (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontSize: 'inherit' }}
                      onClick={() => navigate(`/work-orders/${orderDisplayId(selectedPayment)}`)}
                    >
                      {orderLabel(selectedPayment) || `Order #${orderDisplayId(selectedPayment)}`}
                    </Button>
                  ) : (
                    '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Amount</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedPayment ? formatCurrency(selectedPayment.amount, '—') : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Unapplied</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: selectedPayment && paymentUnapplied(selectedPayment) > 0 ? 'warning.main' : 'inherit',
                  }}
                >
                  {selectedPayment ? formatCurrency(paymentUnapplied(selectedPayment), '—') : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Method</Typography>
                <Typography variant="body2">{selectedPayment?.payment_method ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption">Recorded by</Typography>
                <Typography variant="body2">{recordedByName(selectedPayment)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption">Reference</Typography>
                <Typography variant="body2">{selectedPayment?.reference_number ?? '—'}</Typography>
              </Box>
              <Box sx={{ minWidth: 72, maxWidth: 200 }}>
                <Typography variant="caption">Applied to Invoices</Typography>
                <Typography variant="body2" component="div">
                  {allocatedInvoicesList(selectedPayment || {}).length === 0 ? (
                    '—'
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.15 }}>
                      {allocatedInvoicesList(selectedPayment).map((inv) => (
                        <Button
                          key={inv.id}
                          size="small"
                          variant="text"
                          sx={{
                            p: 0,
                            minWidth: 'auto',
                            minHeight: 0,
                            textTransform: 'none',
                            fontSize: 'inherit',
                            lineHeight: 1.25,
                            justifyContent: 'flex-start',
                          }}
                          onClick={() => navigate(`/customer-invoices/${inv.id}`)}
                        >
                          {inv.label}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setSelectedPayment(null)}
              disabled={!selectedPayment}
              sx={{ flexShrink: 0 }}
              title="Clear selection"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              minWidth: 0,
              width: '100%',
              mt: 0.375,
              pt: 0.375,
              pb: 0.125,
              borderTop: '1px solid',
              borderColor: 'divider',
              maxHeight: 52,
              boxSizing: 'border-box',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <ProcurementSummaryNotesDense label="Notes" text={selectedPayment?.notes} />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Customer"
            >
              <MenuItem value="">All</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={loadPayments}>
            Refresh
          </Button>
          {hasActiveFilters ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
          ) : null}
          {customerFilter && customerCreditAvailable != null && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Customer credit (unapplied): {formatCurrency(customerCreditAvailable)}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>
            {paymentsTotal === 0
              ? 'No payments match the current filters.'
              : payments.length === 0
                ? `Page ${page + 1} · ${paymentsTotal} payment${paymentsTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + payments.length} of ${paymentsTotal} payment${paymentsTotal === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          overflowX: 'auto',
          scrollbarGutter: 'stable'
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            '& .MuiTableCell-head': { verticalAlign: 'top' },
            '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
              top: -13,
              zIndex: 20,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              backgroundClip: 'padding-box',
              opacity: 1,
              overflow: 'hidden',
              borderTop: '1px solid',
              borderTopColor: 'background.paper',
              boxShadow: (theme) =>
                `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
            }
          }}
        >
          <TableHead>
            <TableRow>
              {[
                { key: 'paymentNo', filterKey: 'paymentNo', sortKeyName: 'payment_number', label: 'Payment #' },
                { key: 'customer', filterKey: 'customer', sortKeyName: 'customer', label: 'Customer' },
                { key: 'order', filterKey: 'order', sortKeyName: 'work_order', label: 'Order' },
                { key: 'invoice', filterKey: 'invoice', sortKeyName: 'invoices', label: 'Invoice' },
                { key: 'date', filterKey: 'date', sortKeyName: 'payment_date', label: 'Date' },
                { key: 'amount', filterKey: 'amount', sortKeyName: 'amount', label: 'Amount', align: 'right' },
                { key: 'applied', filterKey: 'applied', sortKeyName: 'applied', label: 'Applied', align: 'right' },
                { key: 'unapplied', filterKey: 'unapplied', sortKeyName: 'unapplied', label: 'Unapplied', align: 'right' },
                { key: 'method', filterKey: 'method', sortKeyName: 'payment_method', label: 'Method' },
                { key: 'recordedBy', filterKey: 'recordedBy', sortKeyName: 'recorded_by', label: 'Recorded by' },
                { key: 'reference', filterKey: 'reference', sortKeyName: 'reference_number', label: 'Reference' },
                { key: 'notes', filterKey: 'notes', sortKeyName: 'notes', label: 'Notes' }
              ].map((col) => (
                <ResizableHeaderCell
                  key={col.key}
                  width={colWidths[col.key]}
                  align={col.align}
                  onResizeStart={(e) => startResize(col.key, e)}
                >
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel
                        active={sortKey === col.sortKeyName}
                        direction={sortDirection}
                        onClick={() => handleSort(col.sortKeyName)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters[col.filterKey]}
                      onChange={(e) =>
                        setHeaderColFilters((p) => ({ ...p, [col.filterKey]: e.target.value }))
                      }
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
              ))}
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)} align="right">
                Actions
              </ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : displayedPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                  No payments found. Click &quot;Record Payment&quot; to add one.
                </TableCell>
              </TableRow>
            ) : (
              displayedPayments.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedPayment?.id === p.id ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => setSelectedPayment(p)}
                    onDoubleClick={() => navigate(`/customer-payments/${p.id}`)}
                  >
                    <TableCell sx={{ width: colWidths.paymentNo, fontWeight: 500 }}>{p.payment_number}</TableCell>
                    <TableCell sx={{ width: colWidths.customer }}>{customerName(p)}</TableCell>
                    <TableCell sx={{ width: colWidths.order }}>
                      {orderDisplayId(p) != null ? (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/work-orders/${orderDisplayId(p)}`);
                          }}
                        >
                          {orderLabel(p) || `Order #${orderDisplayId(p)}`}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.invoice, verticalAlign: 'top' }}>
                      {allocatedInvoicesList(p).length === 0 ? (
                        '—'
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                          {allocatedInvoicesList(p).map((inv) => (
                            <Button
                              key={inv.id}
                              size="small"
                              variant="text"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer-invoices/${inv.id}`);
                              }}
                            >
                              {inv.label}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.date }}>{formatPaymentDateTime(p)}</TableCell>
                    <TableCell sx={{ width: colWidths.amount }} align="right">{formatCurrency(p.amount, '—')}</TableCell>
                    <TableCell sx={{ width: colWidths.applied }} align="right">{formatCurrency(paymentAllocated(p), '—')}</TableCell>
                    <TableCell
                      sx={{ width: colWidths.unapplied, fontWeight: paymentUnapplied(p) > 0 ? 600 : 400 }}
                      align="right"
                    >
                      {formatCurrency(paymentUnapplied(p), '—')}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.method }}>{p.payment_method || '—'}</TableCell>
                    <TableCell sx={{ width: colWidths.recordedBy }}>{recordedByName(p)}</TableCell>
                    <TableCell sx={{ width: colWidths.reference }}>{p.reference_number || '—'}</TableCell>
                    <TableCell
                      sx={{
                        width: colWidths.notes,
                        maxWidth: colWidths.notes,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={p.notes || undefined}
                    >
                      {p.notes?.trim() ? p.notes : '—'}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.actions }} align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/customer-payments/${p.id}`)}
                        title="View"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete payment"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100, 200]}
          count={paymentsTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            try {
              window.localStorage.setItem('wm_customer_payments_rows_per_page', String(next));
            } catch {
              // ignore
            }
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleteSubmitting && setDeleteTarget(null)}>
        <DialogTitle>Delete this payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget
              ? `${deleteTarget.payment_number} (${formatCurrency(deleteTarget.amount, '—')}) will be removed.`
              : ''}
            {deleteTarget && allocatedInvoicesList(deleteTarget).length > 0
              ? ' Invoice allocations will be reversed.'
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmListDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <RecordPaymentDialog
        open={recordDialogOpen}
        onClose={() => setRecordDialogOpen(false)}
        onSuccess={handleRecordSuccess}
      />
    </Box>
  );
};

export default CustomerPayments;
